import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HeartPulse, Sparkles, Waves, ArrowRight, Star, ShieldCheck, Clock, CalendarCheck } from 'lucide-react';
import { Reveal, useTilt, useMouseParallax } from '../motion';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1600948836101-f9ffda59d250?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000';
const AVATARS = [
  'https://images.unsplash.com/photo-1580489944761-15a19d654956?crop=faces&cs=srgb&fm=jpg&q=80&w=120&h=120&fit=crop',
  'https://images.unsplash.com/photo-1489278353717-f64c6ee8a4d2?crop=faces&cs=srgb&fm=jpg&q=80&w=120&h=120&fit=crop',
  'https://images.unsplash.com/photo-1662850886700-4ec19bd30d11?crop=faces&cs=srgb&fm=jpg&q=80&w=120&h=120&fit=crop',
];

const Hero = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stageRef = useMouseParallax();
  const tiltRef = useTilt(6);

  const ctas = [
    {
      id: 'healthcare',
      label: t('hero.healthcare', 'Healthcare'),
      icon: HeartPulse,
      to: '/healthcare-providers?category=Healthcare%20Clinic',
      className:
        'bg-gradient-to-r from-blue-600 to-blue-500 text-white shadow-lg shadow-blue-600/25 hover:shadow-xl hover:shadow-blue-600/30',
    },
    {
      id: 'beauty',
      label: t('hero.beauty', 'Beauty'),
      icon: Sparkles,
      to: '/healthcare-providers?category=Beauty%20Clinic',
      className:
        'bg-white/80 dark:bg-white/5 text-gray-900 dark:text-white border border-gray-200 dark:border-white/15 hover:border-pink-300 dark:hover:border-pink-400/40',
    },
    {
      id: 'wellness',
      label: t('hero.wellness', 'Wellness'),
      icon: Waves,
      to: '/healthcare-providers?category=Spa',
      className:
        'bg-white/80 dark:bg-white/5 text-gray-900 dark:text-white border border-gray-200 dark:border-white/15 hover:border-teal-300 dark:hover:border-teal-400/40',
    },
  ];

  return (
    <section
      ref={stageRef}
      data-testid="hero-section"
      className="relative overflow-hidden bg-white dark:bg-gray-950 grain"
    >
      <div className="absolute inset-0 dot-grid opacity-[0.55] [mask-image:radial-gradient(70%_60%_at_50%_0%,#000,transparent)]" />
      <span className="aurora-blob w-[34rem] h-[34rem] -top-40 -start-32 bg-blue-400/50 dark:bg-blue-600/40 animate-aurora" />
      <span className="aurora-blob w-[28rem] h-[28rem] top-24 end-[-6rem] bg-emerald-400/45 dark:bg-emerald-500/30 animate-aurora-slow" />
      <span className="aurora-blob w-[22rem] h-[22rem] bottom-[-8rem] start-1/3 bg-teal-300/45 dark:bg-teal-500/25 animate-aurora" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-24 lg:pt-24 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Copy */}
          <div className="lg:col-span-6 xl:col-span-7 text-start">
            <Reveal className="inline-flex items-center gap-2 rounded-full glass-chip px-3.5 py-1.5">
              <span className="relative flex w-2 h-2">
                <span className="absolute inset-0 rounded-full bg-emerald-500 animate-ping opacity-70" />
                <span className="relative w-2 h-2 rounded-full bg-emerald-500" />
              </span>
              <span className="text-xs font-semibold tracking-wide text-gray-700 dark:text-gray-200">
                {t('landing.badge', "Kuwait's #1 booking platform")}
              </span>
            </Reveal>

            <Reveal delay={90}>
              <h1
                data-testid="hero-headline"
                className="font-display mt-6 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-gray-900 dark:text-white"
              >
                {t('hero.title', 'Book')}{' '}
                <span className="text-gradient-brand">{t('hero.healthcare', 'Healthcare')}</span>,{' '}
                <span className="text-gradient-brand">{t('hero.beauty', 'Beauty')}</span>{' '}
                <span className="text-gray-400 dark:text-gray-500 font-light">&</span>{' '}
                <span className="text-gradient-brand">{t('hero.wellness', 'Wellness')}</span>
              </h1>
            </Reveal>

            <Reveal delay={170}>
              <p className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                {t('hero.subtitle', 'One platform for all your health and beauty appointments in Kuwait')}
              </p>
            </Reveal>

            <Reveal delay={250} className="mt-9 flex flex-wrap items-center gap-3">
              {ctas.map((cta) => {
                const Icon = cta.icon;
                return (
                  <button
                    key={cta.id}
                    onClick={() => navigate(cta.to)}
                    data-testid={`hero-cta-${cta.id}`}
                    className={`btn-shine lift group inline-flex items-center gap-2.5 rounded-full px-6 py-3.5 text-sm font-semibold backdrop-blur-sm ${cta.className}`}
                  >
                    <Icon className="w-[18px] h-[18px]" />
                    {cta.label}
                    <ArrowRight className="w-4 h-4 opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all duration-300 rtl:rotate-180" />
                  </button>
                );
              })}
            </Reveal>

            <Reveal delay={330} className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-4">
              <div className="flex items-center">
                <div className="flex -space-x-3 rtl:space-x-reverse">
                  {AVATARS.map((src) => (
                    <img
                      key={src}
                      src={src}
                      alt=""
                      loading="lazy"
                      className="w-9 h-9 rounded-full object-cover ring-2 ring-white dark:ring-gray-950"
                    />
                  ))}
                </div>
                <div className="ms-3">
                  <div className="flex items-center gap-1">
                    {[0, 1, 2, 3, 4].map((i) => (
                      <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {t('landing.trustedBy', 'Trusted by 3,500+ people across Kuwait')}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-300">
                <ShieldCheck className="w-4 h-4 text-emerald-500" />
                {t('landing.verifiedOnly', 'Verified providers only')}
              </div>
            </Reveal>
          </div>

          {/* 3D booking card */}
          <div className="lg:col-span-6 xl:col-span-5">
            <Reveal delay={200} className="tilt-stage relative mx-auto max-w-md lg:max-w-none">
              <span className="absolute -inset-6 rounded-[2.5rem] bg-gradient-to-tr from-blue-500/25 via-teal-400/20 to-emerald-500/25 blur-2xl animate-halo-pulse" />

              <div
                ref={tiltRef}
                className="tilt-card relative rounded-[2rem] glass-panel p-4 sm:p-5 parallax-layer-soft"
              >
                <div className="relative overflow-hidden rounded-[1.5rem]">
                  <img
                    src={HERO_IMAGE}
                    alt={t('landing.demoProvider', 'Serenity Spa & Salon')}
                    className="w-full h-52 sm:h-60 object-cover"
                  />
                  <span className="absolute inset-0 bg-gradient-to-t from-gray-950/70 via-gray-950/10 to-transparent" />
                  <div className="absolute bottom-4 start-4 end-4 text-white">
                    <p className="text-[11px] font-semibold uppercase tracking-widest text-white/70">
                      {t('siteNav.salons', 'Salons')}
                    </p>
                    <p className="font-display text-lg font-bold leading-tight mt-1">
                      {t('landing.demoProvider', 'Serenity Spa & Salon')}
                    </p>
                    <p className="text-xs text-white/80 mt-1">{t('landing.demoArea', 'Salmiya, Kuwait')}</p>
                  </div>
                </div>

                <div className="mt-4 space-y-2.5">
                  <div className="flex items-center justify-between rounded-2xl bg-gray-50 dark:bg-white/5 px-4 py-3">
                    <span className="flex items-center gap-2 text-[13px] font-medium text-gray-700 dark:text-gray-200">
                      <Sparkles className="w-4 h-4 text-pink-500" />
                      {t('landing.demoService', 'Hydrating facial · 60 min')}
                    </span>
                    <span className="text-[13px] font-semibold text-gray-900 dark:text-white numeric-ltr">18 KD</span>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {['10:30', '12:00', '16:45'].map((slot, i) => (
                      <span
                        key={slot}
                        className={`rounded-xl px-2 py-2.5 text-center text-[13px] font-semibold numeric-ltr transition-colors ${
                          i === 1
                            ? 'bg-gradient-to-r from-blue-600 to-emerald-500 text-white shadow-md shadow-blue-600/25'
                            : 'bg-gray-50 dark:bg-white/5 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {slot}
                      </span>
                    ))}
                  </div>
                  <button
                    onClick={() => navigate('/healthcare-providers')}
                    data-testid="hero-demo-book-button"
                    className="btn-shine w-full rounded-2xl bg-gray-900 dark:bg-white px-4 py-3.5 text-sm font-semibold text-white dark:text-gray-900 hover:opacity-90 transition-opacity"
                  >
                    {t('landing.demoBook', 'Confirm booking')}
                  </button>
                </div>
              </div>

              <div className="absolute -top-5 end-2 sm:end-[-1.5rem] rounded-2xl glass-chip px-3.5 py-2.5 shadow-lg parallax-layer animate-float-slow">
                <div className="flex items-center gap-2">
                  <CalendarCheck className="w-4 h-4 text-emerald-500" />
                  <div>
                    <p className="text-[11px] font-semibold text-gray-900 dark:text-white leading-none">
                      {t('landing.demoConfirmed', 'Booking confirmed')}
                    </p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                      {t('landing.demoConfirmedSub', 'WhatsApp & email sent')}
                    </p>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-6 start-2 sm:start-[-2rem] rounded-2xl glass-chip px-3.5 py-2.5 shadow-lg parallax-layer animate-float-slower">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-600" />
                  <div>
                    <p className="text-[11px] font-semibold text-gray-900 dark:text-white leading-none numeric-ltr">
                      24/7
                    </p>
                    <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">
                      {t('landing.demoInstant', 'Instant confirmation')}
                    </p>
                  </div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
